import React, { useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import "./Dashboard.scss"; // Import the CSS file

//import context
import { MyContext } from "../../../../tenantsDashContext";

//Tour utilities
import {
  tourSteps,
  getCurrentTourStep,
  setTourStep as setTourStepUtil,
  isInTourMode,
  completeTour,
  getTourStepDescription,
  skipTourStep,
  stepRequiresCompletion,
} from "../../../../utils/tourUtils";

//icons
import { RxDashboard } from "react-icons/rx";
import { MdPayment } from "react-icons/md";
import { BiReceipt } from "react-icons/bi";
import { AiOutlinePieChart } from "react-icons/ai";
import { FaUsers } from "react-icons/fa";
import { LiaFileInvoiceSolid } from "react-icons/lia";
import { RiMessage2Line } from "react-icons/ri";
import { SlLogout } from "react-icons/sl";
import { IoPersonOutline } from "react-icons/io5";
import { HiOutlineBuildingOffice2 } from "react-icons/hi2";

//assets
import Logo from "../../../../resources/logo.png";

//import resize function
import { vw, vh } from "../../../assets/reusableComponents/resizeFunction";

//imports for user data
import { useSelector, useDispatch } from "react-redux";

//functions for datat fetching
import { handleLogout } from "../../../../redux/slices/sliceActions";

const ROUTE_SEGMENT_MAP = {
  Overview: "overview",
  Properties: "properties",
  Tenants: "tenants",
  Invoices: "invoices",
  Payments: "payments",
  Expenses: "expenses",
  Reports: "reports",
  Messages: "messages",
  Profile: "profile",
};

const Navigation = () => {
  const { sharedValue, setSharedValue } = useContext(MyContext);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [activeItem, setActiveItem] = useState("Overview"); // State to keep track of the active item
  const [isFirstTime, setIsFirstTime] = useState(false);
  const [tourStep, setTourStep] = useState(0);

  useEffect(() => {
    if (sharedValue) {
      setActiveItem(sharedValue);
    }
  }, [sharedValue]);

  useEffect(() => {
    const firstTimeFlag = isInTourMode();
    const savedTourStep = getCurrentTourStep();

    console.log("is first time", firstTimeFlag);
    console.log("saved tour step", savedTourStep);

    if (firstTimeFlag) {
      setIsFirstTime(true);
      setTourStep(savedTourStep);
      // Set active item to the current tour step
      const initialSection = tourSteps[savedTourStep] || "Profile";
      setActiveItem(initialSection);
      setSharedValue(initialSection);

      const routeSegment = ROUTE_SEGMENT_MAP[initialSection];
      if (routeSegment) {
        navigate(`/auth/landlord/${routeSegment}`, { replace: true });
      }
    }
  }, []);

  // Function to advance to next tour step
  const advanceTourStep = () => {
    const nextStep = tourStep + 1;
    if (nextStep < tourSteps.length) {
      setTourStep(nextStep);
      setTourStepUtil(nextStep); // Store in localStorage using utility
      setActiveItem(tourSteps[nextStep]);
      setSharedValue(tourSteps[nextStep]);

      const routeSegment = ROUTE_SEGMENT_MAP[tourSteps[nextStep]];
      if (routeSegment) {
        navigate(`/auth/landlord/${routeSegment}`);
      }
    } else {
      // Tour completed
      setIsFirstTime(false);
      completeTour(); // Use utility function
    }
  };

  // Function to manually advance tour (for external use)
  const manualAdvanceTour = () => {
    advanceTourStep();
  };

  // Expose tour functions to window for external access
  useEffect(() => {
    if (isFirstTime) {
      window.advanceRentlyTour = manualAdvanceTour;
    }
    return () => {
      if (window.advanceRentlyTour) {
        delete window.advanceRentlyTour;
      }
    };
  }, [isFirstTime, tourStep]);

  // Function to check if a nav item should be visible during the tour
  const shouldShowNavItem = (itemName) => {
    if (!isFirstTime) return true; // Show all items if not in tour mode

    const currentStepIndex = tourStep;
    const itemStepIndex = tourSteps.indexOf(itemName);

    // Always show Profile and Logout
    if (
      itemName === "Profile" ||
      itemName === "Logout" ||
      itemName === "Messages"
    ) {
      return true;
    }

    // Show items up to current step
    return itemStepIndex !== -1 && itemStepIndex <= currentStepIndex;
  };

  const handleNavItemClick = (item) => {
    if (item === "Logout") {
      dispatch(handleLogout());
      localStorage.removeItem("user");
      localStorage.removeItem("isFirstTime");
      localStorage.removeItem("tourStep");
      window.location.href = "/signin";
      return;
    }

    // During tour mode, don't auto-advance - let components handle progression
    // Only allow navigation if the step is unlocked
    if (isFirstTime) {
      const itemStepIndex = tourSteps.indexOf(item);
      const currentStepIndex = tourStep;

      // Allow navigation to current step or previously unlocked steps
      if (itemStepIndex > currentStepIndex) {
        // Don't allow navigation to locked steps
        return;
      }
    }

    if (sharedValue === item) {
      setSharedValue(""); // Briefly set sharedValue to an empty string
      setTimeout(() => {
        setSharedValue(item); // Set it back to the clicked item
      }, 0); // You can adjust the timeout duration if needed
    } else {
      setSharedValue(item);
    }

    setActiveItem(item); // Update the active item state when a nav item is clicked

    const routeSegment = ROUTE_SEGMENT_MAP[item];
    if (routeSegment) {
      navigate(`/auth/landlord/${routeSegment}`);
    }
  };

  return (
    <nav className="dashboard-nav">
      <div className="top-nav-section">
        <div>
          <div className="dashboard-header">
            <img src={Logo} alt="website logo" height={"55px"} />
            <div className="company-name">Rentlybuzz</div>
          </div>
          <ul className="dashboard-list">
            <li
              className={`dashboard-item ${
                activeItem === "Overview" ? "active" : ""
              }`}
              onClick={() => handleNavItemClick("Overview")}
              style={{
                display: shouldShowNavItem("Overview") ? "flex" : "none",
              }}
            >
              <div className="text-and-icon">
                <RxDashboard className="dashboard-icon" />
                <div className="text">Overview</div>
              </div>
              <div className="box"></div>
            </li>
            <li
              className={`dashboard-item ${
                activeItem === "Properties" ? "active" : ""
              }`}
              onClick={() => handleNavItemClick("Properties")}
              style={{
                display: shouldShowNavItem("Properties") ? "flex" : "none",
              }}
            >
              <div className="text-and-icon">
                <HiOutlineBuildingOffice2 className="dashboard-icon" />
                <div className="text">Properties</div>
              </div>
              <div className="box"></div>
            </li>
            <li
              className={`dashboard-item ${
                activeItem === "Tenants" ? "active" : ""
              }`}
              onClick={() => handleNavItemClick("Tenants")}
              style={{
                display: shouldShowNavItem("Tenants") ? "flex" : "none",
              }}
            >
              <div className="text-and-icon">
                <FaUsers className="dashboard-icon" />
                <div className="text">Tenants</div>
              </div>
              <div className="box"></div>
            </li>
            <li
              className={`dashboard-item ${
                activeItem === "Invoices" ? "active" : ""
              }`}
              onClick={() => handleNavItemClick("Invoices")}
              style={{
                display: shouldShowNavItem("Invoices") ? "flex" : "none",
              }}
            >
              <div className="text-and-icon">
                <LiaFileInvoiceSolid className="dashboard-icon" />
                <div className="text">Invoices</div>
              </div>
              <div className="box"></div>
            </li>
            <li
              className={`dashboard-item ${
                activeItem === "Payments" ? "active" : ""
              }`}
              onClick={() => handleNavItemClick("Payments")}
              style={{
                display: shouldShowNavItem("Payments") ? "flex" : "none",
              }}
            >
              <div className="text-and-icon">
                <MdPayment className="dashboard-icon" />
                <div className="text">Payments</div>
              </div>
              <div className="box"></div>
            </li>
            <li
              className={`dashboard-item ${
                activeItem === "Expenses" ? "active" : ""
              }`}
              onClick={() => handleNavItemClick("Expenses")}
              style={{
                display: shouldShowNavItem("Expenses") ? "flex" : "none",
              }}
            >
              <div className="text-and-icon">
                <BiReceipt className="dashboard-icon" />
                <div className="text">Expenses</div>
              </div>
              <div className="box"></div>
            </li>
            <li
              className={`dashboard-item ${
                activeItem === "Reports" ? "active" : ""
              }`}
              onClick={() => handleNavItemClick("Reports")}
              style={{
                display: shouldShowNavItem("Reports") ? "flex" : "none",
              }}
            >
              <div className="text-and-icon">
                <AiOutlinePieChart className="dashboard-icon" />
                <div className="text">Reports</div>
              </div>
              <div className="box"></div>
            </li>

            <li
              className={`dashboard-item ${
                activeItem === "Messages" ? "active" : ""
              }`}
              onClick={() => handleNavItemClick("Messages")}
              style={{
                display: shouldShowNavItem("Messages") ? "flex" : "none",
              }}
            >
              <div className="text-and-icon">
                <RiMessage2Line className="dashboard-icon" />
                <div className="text">Messages</div>
              </div>
              <div className="box"></div>
            </li>
          </ul>
        </div>
        <div>
          <ul className={"dashboard-list profile-nav-container"}>
            <li
              className={`dashboard-item ${
                activeItem === "Profile" ? "active" : ""
              }`}
              onClick={() => handleNavItemClick("Profile")}
            >
              <div className="text-and-icon">
                <IoPersonOutline className="dashboard-icon" />
                <div className="text">Profile</div>
              </div>
              <div className="box"></div>
            </li>
            <li
              className={`dashboard-item ${
                activeItem === "Logout" ? "active" : ""
              }`}
              onClick={() => handleNavItemClick("Logout")}
            >
              <div className="text-and-icon">
                <SlLogout className={`dashboard-icon logout`} />
                <div className="text">Logout</div>
              </div>
              <div className="box"></div>
            </li>
          </ul>
        </div>
      </div>
      <div className="dashboard-list">
        <li className={`dashboard-item email`}>help@rentlybuzz.com</li>
      </div>

      {/* Tour Guide Component */}
      {isFirstTime && (
        <div
          className="tour-guide"
          style={{
            position: "fixed",
            bottom: vw(20),
            right: vw(20),
            backgroundColor: "#1A2A3A",
            color: "#fff",
            padding: `${vh(15)} ${vw(15)}`,
            borderRadius: vw(8),
            border: `${vh(2)} solid #B39359`,
            boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
            zIndex: 1000,

            fontSize: vh(14),
            fontFamily: '"Poppins", sans-serif',
            fontWeight: 300,
          }}
        >
          <div
            style={{
              fontWeight: "bold",
              marginBottom: vh(8),
              color: "#B39359",
              fontSize: vh(16),
            }}
          >
            Setup Guide - Step {tourStep + 1} of {tourSteps.length}
          </div>
          <div style={{ lineHeight: "1.4" }}>
            {(() => {
              const getIconAndText = (step) => {
                const iconStyle = {
                  marginRight: vw(8),
                  fontSize: vh(16),
                  color: "#B39359",
                };

                switch (step) {
                  case 0:
                    return (
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <IoPersonOutline style={iconStyle} size={24} />
                        Update your profile information to proceed to Properties
                      </div>
                    );
                  case 1:
                    return (
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <HiOutlineBuildingOffice2 style={iconStyle} size={24} />
                        Create your first property and add houses to proceed to
                        Tenants
                      </div>
                    );
                  case 2:
                    return (
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <FaUsers style={iconStyle} size={24} />
                        Add your first tenant to proceed to Payments
                      </div>
                    );
                  case 3:
                    return (
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <MdPayment style={iconStyle} size={24} />
                        Check how payments work in your system
                      </div>
                    );
                  case 4:
                    return (
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <LiaFileInvoiceSolid style={iconStyle} size={24} />
                        Learn about invoices and billing
                      </div>
                    );
                  case 5:
                    return (
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <BiReceipt style={iconStyle} size={24} />
                        Manage your expenses and costs
                      </div>
                    );
                  case 6:
                    return (
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <RxDashboard style={iconStyle} size={24} />
                        View your dashboard overview
                      </div>
                    );
                  default:
                    return "Welcome to your setup guide!";
                }
              };

              return getIconAndText(tourStep);
            })()}
          </div>

          {stepRequiresCompletion(tourStep) ? (
            <div
              style={{
                marginTop: vh(10),
                fontSize: vh(12),
                opacity: "0.9",
                fontStyle: "italic",
              }}
            >
              Complete the required actions to proceed automatically
            </div>
          ) : (
            <div style={{ marginTop: vh(10), display: "flex", gap: vw(8) }}>
              <button
                onClick={() => {
                  advanceTourStep();
                }}
                style={{
                  backgroundColor: "#B39359",
                  border: "none",
                  color: "#1A2A3A",
                  padding: `${vh(8)} ${vw(12)}`,
                  borderRadius: vw(4),
                  fontSize: vh(12),
                  fontFamily: '"Poppins", sans-serif',
                  fontWeight: "500",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = "#fff";
                  e.target.style.transform = "scale(1.05)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "#B39359";
                  e.target.style.transform = "scale(1)";
                }}
              >
                Next
              </button>
              <button
                onClick={() => {
                  advanceTourStep();
                }}
                style={{
                  backgroundColor: "transparent",
                  border: `${vh(1)} solid #6c757d`,
                  color: "#6c757d",
                  padding: `${vh(6)} ${vw(10)}`,
                  borderRadius: vw(4),
                  fontSize: vh(12),
                  fontFamily: '"Poppins", sans-serif',
                  fontWeight: "300",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = "#6c757d";
                  e.target.style.color = "#fff";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "transparent";
                  e.target.style.color = "#6c757d";
                }}
              >
                Skip This Step
              </button>
              <button
                onClick={() => {
                  setIsFirstTime(false);
                  completeTour();
                }}
                style={{
                  backgroundColor: "transparent",
                  border: `${vh(1)} solid #B39359`,
                  color: "#B39359",
                  padding: `${vh(6)} ${vw(10)}`,
                  borderRadius: vw(4),
                  fontSize: vh(12),
                  fontFamily: '"Poppins", sans-serif',
                  fontWeight: "300",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = "#B39359";
                  e.target.style.color = "#1A2A3A";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "transparent";
                  e.target.style.color = "#B39359";
                }}
              >
                Skip Tour
              </button>
            </div>
          )}
        </div>
      )}
    </nav>
  );
};

export default Navigation;
